import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import './OwnerRecordsPage.css';

interface OwnerHistoryEntry {
  id: string;
  user_id: string;
  league_name: string;
  league_type: string;
  team_name: string;
  season_year: number;
  final_rank: number;
  wins: number;
  losses: number;
  prize_won: number;
  entry_fee: number;
  completed_at: string;
}

interface OwnerStats {
  user_id: string;
  username: string;
  leagues_entered: number;
  total_prizes: number;
  total_entry_fees: number;
  net_profit: number;
  avg_finish: number;
  best_finish: number;
  wins: number;
  losses: number;
  win_pct: number;
  first_place: number;
  top_three: number;
}

type SortKey = 'total_prizes' | 'net_profit' | 'avg_finish' | 'leagues_entered' | 'win_pct' | 'first_place';

const OwnerRecordsPage: React.FC = () => {
  const [ownerStats, setOwnerStats] = useState<OwnerStats[]>([]);
  const [recentEntries, setRecentEntries] = useState<OwnerHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('total_prizes');
  const [activeTab, setActiveTab] = useState<'leaderboard' | 'history'>('leaderboard');

  useEffect(() => {
    if (isSupabaseConfigured() && supabase) {
      loadData();
    }
  }, []);

  async function loadData() {
    // Get all owner_history entries
    const { data: history } = await supabase!.from('owner_history')
      .select('*')
      .order('completed_at', { ascending: false });

    // Get recent entries for history tab
    if (history && history.length > 0) {
      setRecentEntries((history as OwnerHistoryEntry[]).slice(0, 50));
    }

    if (!history || history.length === 0) {
      setLoading(false);
      return;
    }

    // Aggregate stats per user
    const statsMap = new Map<string, OwnerStats>();

    for (const entry of history as OwnerHistoryEntry[]) {
      const existing = statsMap.get(entry.user_id);
      if (existing) {
        existing.leagues_entered += 1;
        existing.total_prizes += entry.prize_won || 0;
        existing.total_entry_fees += entry.entry_fee || 0;
        existing.net_profit = existing.total_prizes - existing.total_entry_fees;
        existing.avg_finish = ((existing.avg_finish * (existing.leagues_entered - 1)) + entry.final_rank) / existing.leagues_entered;
        existing.best_finish = Math.min(existing.best_finish, entry.final_rank);
        existing.wins += entry.wins || 0;
        existing.losses += entry.losses || 0;
        existing.win_pct = existing.wins / (existing.wins + existing.losses) * 100;
        if (entry.final_rank === 1) existing.first_place += 1;
        if (entry.final_rank <= 3) existing.top_three += 1;
      } else {
        statsMap.set(entry.user_id, {
          user_id: entry.user_id,
          username: 'Loading...',
          leagues_entered: 1,
          total_prizes: entry.prize_won || 0,
          total_entry_fees: entry.entry_fee || 0,
          net_profit: (entry.prize_won || 0) - (entry.entry_fee || 0),
          avg_finish: entry.final_rank,
          best_finish: entry.final_rank,
          wins: entry.wins || 0,
          losses: entry.losses || 0,
          win_pct: (entry.wins || 0) / ((entry.wins || 0) + (entry.losses || 1)) * 100,
          first_place: entry.final_rank === 1 ? 1 : 0,
          top_three: entry.final_rank <= 3 ? 1 : 0,
        });
      }
    }

    // Fetch usernames
    const userIds = Array.from(statsMap.keys());
    if (userIds.length > 0) {
      const { data: users } = await supabase!.from('users')
        .select('id, username')
        .in('id', userIds);
      
      if (users) {
        for (const u of users) {
          const stats = statsMap.get(u.id);
          if (stats) stats.username = u.username || 'Unknown';
        }
      }
    }

    setOwnerStats(Array.from(statsMap.values()));
    setLoading(false);
  }

  const sorted = [...ownerStats].sort((a, b) => {
    switch (sortKey) {
      case 'total_prizes': return b.total_prizes - a.total_prizes;
      case 'net_profit': return b.net_profit - a.net_profit;
      case 'avg_finish': return a.avg_finish - b.avg_finish; // lower is better
      case 'leagues_entered': return b.leagues_entered - a.leagues_entered;
      case 'win_pct': return b.win_pct - a.win_pct;
      case 'first_place': return b.first_place - a.first_place;
      default: return 0;
    }
  });

  const getRankEmoji = (i: number) => {
    if (i === 0) return '🥇';
    if (i === 1) return '🥈';
    if (i === 2) return '🥉';
    return `#${i + 1}`;
  };

  return (
    <div className="owner-records-page">
      <h1>📜 Quick League Records</h1>
      <p className="subtitle">Leaderboard — who's dominating quick leagues.</p>

      <div className="or-tabs">
        <button className={`or-tab ${activeTab === 'leaderboard' ? 'active' : ''}`} onClick={() => setActiveTab('leaderboard')}>🏆 Leaderboard</button>
        <button className={`or-tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>📋 Recent Results</button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : ownerStats.length === 0 && recentEntries.length === 0 ? (
        <div className="no-records">
          <p>No quick league results yet. Join a quick league to start building your record!</p>
        </div>
      ) : activeTab === 'leaderboard' ? (
        <div className="leaderboard-section">
          <div className="sort-bar">
            <span className="sort-label">Sort by:</span>
            {[
              { key: 'total_prizes' as SortKey, label: '💰 $BALLS Won' },
              { key: 'net_profit' as SortKey, label: '📈 Net Profit' },
              { key: 'avg_finish' as SortKey, label: '🎯 Avg Finish' },
              { key: 'leagues_entered' as SortKey, label: '🎮 Leagues' },
              { key: 'win_pct' as SortKey, label: '🏆 Win %' },
              { key: 'first_place' as SortKey, label: '👑 1st Places' },
            ].map(s => (
              <button key={s.key} className={`sort-btn ${sortKey === s.key ? 'active' : ''}`} onClick={() => setSortKey(s.key)}>
                {s.label}
              </button>
            ))}
          </div>

          {ownerStats.length === 0 ? (
            <div className="no-records"><p>No data yet.</p></div>
          ) : (
            <div className="leaderboard-grid">
              {sorted.map((owner, i) => (
                <div key={owner.user_id} className={`lb-card ${i < 3 ? 'top-three' : ''}`}>
                  <div className="lb-rank">{getRankEmoji(i)}</div>
                  <div className="lb-info">
                    <span className="lb-name">{owner.username}</span>
                    <div className="lb-stats">
                      <span className="lb-stat"><span className="lb-stat-val">{owner.total_prizes.toLocaleString()}</span> $BALLS won</span>
                      <span className={`lb-stat ${owner.net_profit >= 0 ? 'positive' : 'negative'}`}><span className="lb-stat-val">{owner.net_profit >= 0 ? '+' : ''}{owner.net_profit.toLocaleString()}</span> net</span>
                      <span className="lb-stat"><span className="lb-stat-val">#{owner.avg_finish.toFixed(1)}</span> avg finish</span>
                      <span className="lb-stat"><span className="lb-stat-val">{owner.leagues_entered}</span> leagues</span>
                      <span className="lb-stat"><span className="lb-stat-val">{owner.win_pct.toFixed(0)}%</span> win rate</span>
                      <span className="lb-stat"><span className="lb-stat-val">{owner.first_place}</span> 🏆 first</span>
                      <span className="lb-stat"><span className="lb-stat-val">{owner.top_three}</span> top 3</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="history-section">
          <table className="records-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>League</th>
                <th>Team</th>
                <th>Record</th>
                <th>Prize</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {recentEntries.map(r => (
                <tr key={r.id}>
                  <td className="rank">
                    {r.final_rank === 1 ? '🥇' : r.final_rank === 2 ? '🥈' : r.final_rank === 3 ? '🥉' : `#${r.final_rank}`}
                  </td>
                  <td>{r.league_name}</td>
                  <td style={{ color: '#60a5fa' }}>{r.team_name}</td>
                  <td>{r.wins}-{r.losses}</td>
                  <td className={r.prize_won > 0 ? 'prize-positive' : ''}>
                    {r.prize_won > 0 ? `+${r.prize_won.toLocaleString()}` : '—'}
                  </td>
                  <td className="tx-date">{r.completed_at ? new Date(r.completed_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OwnerRecordsPage;
